import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAdminUsers } from "../../../../Actions/AdminActions";
import { formatApiError } from "../../../../Actions/Utils";

const PAGE_SIZE = 20;

function Badge({ value }) {
  const color =
    value === "Internal"
      ? "bg-indigo-100 text-indigo-700"
      : "bg-emerald-100 text-emerald-700";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {value}
    </span>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );
}

export default function UserList() {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({ phone: "", name: "", user_type: "" });
  const [applied, setApplied] = useState({ phone: "", name: "", user_type: "" });
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchUsers = useCallback(async (currentFilters, currentPage) => {
    setLoading(true);
    setError("");
    const res = await getAdminUsers({ ...currentFilters, page: currentPage, limit: PAGE_SIZE });
    setLoading(false);
    if (res?.status === 200) {
      setUsers(res.data?.items || res.data?.users || res.data?.data || []);
      const totalPages = res.data?.total_pages ?? Math.ceil((res.data?.total || 0) / PAGE_SIZE);
      setTotalPages(Math.max(1, totalPages));
    } else {
      setError(formatApiError(res?.data?.detail) || res?.data?.message || "Failed to load users.");
    }
  }, []);

  useEffect(() => {
    fetchUsers(applied, page);
  }, [applied, page, fetchUsers]);

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    setApplied({ ...filters });
  }

  function handleClear() {
    const empty = { phone: "", name: "", user_type: "" };
    setFilters(empty);
    setApplied(empty);
    setPage(1);
  }

  function handleEdit(user) {
    navigate(`/admin/users/${user.user_id}/edit`, { state: { user } });
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage internal and external users</p>
        </div>
        <button
          onClick={() => navigate("/admin/users/new")}
          className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create User
        </button>
      </div>

      {/* Search filters */}
      <form onSubmit={handleSearch} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
            <input
              type="text"
              placeholder="+91XXXXXXXXXX"
              value={filters.phone}
              onChange={(e) => setFilters((f) => ({ ...f, phone: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
            <input
              type="text"
              placeholder="Search by name"
              value={filters.name}
              onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">User Type</label>
            <select
              value={filters.user_type}
              onChange={(e) => setFilters((f) => ({ ...f, user_type: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              <option value="">All types</option>
              <option value="Internal">Internal</option>
              <option value="External - User">External - User</option>
              <option value="External - Stakeholder">External - Stakeholder</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button
            type="submit"
            className="flex-1 sm:flex-none px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="flex-1 sm:flex-none px-4 py-2 bg-white text-gray-600 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Clear
          </button>
        </div>
      </form>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <Spinner />
        ) : users.length === 0 ? (
          <div className="py-20 text-center text-gray-400 text-sm">
            No users found. Try adjusting your search filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {["Name", "Phone", "Type", "State", "District", "Agents", "Version", "Actions"].map((h) => (
                    <th
                      key={h}
                      className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 sm:px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {user.name || "—"}
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-600 whitespace-nowrap font-mono">
                      {user.phone || "—"}
                    </td>
                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                      <Badge value={user.user_type || "—"} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {user.location?.state?.en || user.state || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {user.location?.district?.en || user.district || "—"}
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-600">
                      {user.agents && user.agents.length > 0 ? (
                        <div className="space-y-0.5">
                          {user.agents.map((a, i) => (
                            <div key={i} className="text-xs text-gray-500">
                              {[a.cadre, a.kb, a.approach].filter(Boolean).join(" · ")}
                            </div>
                          ))}
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-500 whitespace-nowrap font-mono">
                      v{user.version ?? 0}
                    </td>
                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                      <button
                        onClick={() => handleEdit(user)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && users.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
